import { useEffect, useRef, useState, FormEvent } from 'react';

type Status = 'idle' | 'loading' | 'success' | 'error';

const concernOptions = [
  '会議で何も決まらない',
  '決めても実行されない',
  '同じ議題が繰り返される',
  '会議時間が長すぎる',
  '管理職が疲弊している',
  'その他',
];

const sizeOptions = ['30〜50名', '51〜100名', '101〜200名', '201〜300名'];

export default function CTASection() {
  const [visible, setVisible] = useState(false);
  const [status, setStatus] = useState<Status>('idle');
  const [concern, setConcern] = useState('');
  const [size, setSize] = useState('');
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.06 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const textarea = form.querySelector('textarea');
    if (textarea && textarea.value.length > 500) {
      return;
    }
    setStatus('loading');
    const formData = new FormData(form);
    const params = new URLSearchParams();
    formData.forEach((val, key) => { params.append(key, val as string); });

    try {
      const res = await fetch('https://readdy.ai/api/form/d733um1ttkj2t7sc9ij0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      setStatus(res.ok ? 'success' : 'error');
      if (res.ok) form.reset();
    } catch {
      setStatus('error');
    }
  };

  return (
    <section
      ref={ref}
      id="cta"
      className="py-24 px-4 md:px-10 bg-white"
    >
      <div className="max-w-4xl mx-auto">
        {/* Top CTA bar */}
        <div
          className="rounded-lg p-8 md:p-12 mb-12 text-center transition-all duration-700"
          style={{
            backgroundColor: '#C0392B',
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateY(0)' : 'translateY(20px)',
          }}
        >
          <h2
            className="text-2xl md:text-4xl font-bold text-white mb-3 leading-tight"
            style={{ fontFamily: 'Noto Sans JP, sans-serif' }}
          >
            まず、5分で診断してみてください。
          </h2>
          <p
            className="text-base text-white/80 mb-6"
            style={{ fontFamily: 'Noto Sans JP, sans-serif' }}
          >
            あなたの会議の「決断力スコア」を無料で算出します。
          </p>
          <div className="flex flex-wrap justify-center gap-3 text-sm text-white/70" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>
            {['✓ 完全無料', '✓ 5分で完了', '✓ 即日フィードバック', '✓ 強引な営業なし'].map((t) => (
              <span key={t}>{t}</span>
            ))}
          </div>
        </div>

        {/* Form */}
        <div
          className="rounded-lg border border-gray-200 overflow-hidden transition-all duration-700"
          style={{ opacity: visible ? 1 : 0, transitionDelay: '0.2s' }}
        >
          <div className="px-8 py-5 border-b border-gray-200 bg-gray-50 flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#C0392B]/10">
              <i className="ri-survey-line text-[#C0392B] text-base" />
            </div>
            <div>
              <h3
                className="text-base font-bold text-gray-900"
                style={{ fontFamily: 'Noto Sans JP, sans-serif' }}
              >
                会議決断力チェック — 無料診断フォーム
              </h3>
              <p className="text-xs text-gray-400" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>
                所要時間：約5分 / 回答後、担当者より診断レポートをお送りします
              </p>
            </div>
          </div>

          {status === 'success' ? (
            <div className="px-8 py-16 flex flex-col items-center text-center">
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-green-100 mb-5">
                <i className="ri-check-line text-3xl text-green-600" />
              </div>
              <h3
                className="text-lg font-bold text-gray-900 mb-2"
                style={{ fontFamily: 'Noto Sans JP, sans-serif' }}
              >
                診断を受け付けました！
              </h3>
              <p
                className="text-sm text-gray-500 max-w-xs"
                style={{ fontFamily: 'Noto Sans JP, sans-serif' }}
              >
                1営業日以内に診断レポートをメールでお送りします。<br />
                まずは現状を整理してお待ちください。
              </p>
            </div>
          ) : (
            <form
              id="free-diagnosis-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="px-8 py-8 grid grid-cols-1 md:grid-cols-2 gap-5"
            >
              <div>
                <label
                  className="block text-xs font-bold text-gray-700 mb-1.5"
                  style={{ fontFamily: 'Noto Sans JP, sans-serif' }}
                >
                  お名前 <span className="text-[#C0392B]">*</span>
                </label>
                <input
                  name="name"
                  type="text"
                  required
                  placeholder="山田 太郎"
                  className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:border-[#C0392B] transition-colors"
                  style={{ fontFamily: 'Noto Sans JP, sans-serif' }}
                />
              </div>

              <div>
                <label
                  className="block text-xs font-bold text-gray-700 mb-1.5"
                  style={{ fontFamily: 'Noto Sans JP, sans-serif' }}
                >
                  メールアドレス <span className="text-[#C0392B]">*</span>
                </label>
                <input
                  name="email"
                  type="email"
                  required
                  placeholder="taro@example.com"
                  className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:border-[#C0392B] transition-colors"
                  style={{ fontFamily: 'Noto Sans JP, sans-serif' }}
                />
              </div>

              <div>
                <label
                  className="block text-xs font-bold text-gray-700 mb-1.5"
                  style={{ fontFamily: 'Noto Sans JP, sans-serif' }}
                >
                  会社名 <span className="text-[#C0392B]">*</span>
                </label>
                <input
                  name="company"
                  type="text"
                  required
                  placeholder="株式会社〇〇"
                  className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:border-[#C0392B] transition-colors"
                  style={{ fontFamily: 'Noto Sans JP, sans-serif' }}
                />
              </div>

              <div>
                <label
                  className="block text-xs font-bold text-gray-700 mb-1.5"
                  style={{ fontFamily: 'Noto Sans JP, sans-serif' }}
                >
                  従業員数 <span className="text-[#C0392B]">*</span>
                </label>
                <select
                  name="employee_size"
                  required
                  value={size}
                  onChange={(e) => setSize(e.target.value)}
                  className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:border-[#C0392B] transition-colors bg-white cursor-pointer"
                  style={{ fontFamily: 'Noto Sans JP, sans-serif' }}
                >
                  <option value="">選択してください</option>
                  {sizeOptions.map((o) => (
                    <option key={o} value={o}>{o}</option>
                  ))}
                </select>
              </div>

              <div>
                <label
                  className="block text-xs font-bold text-gray-700 mb-1.5"
                  style={{ fontFamily: 'Noto Sans JP, sans-serif' }}
                >
                  役職
                </label>
                <input
                  name="position"
                  type="text"
                  placeholder="代表取締役 / 事業部長 など"
                  className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:border-[#C0392B] transition-colors"
                  style={{ fontFamily: 'Noto Sans JP, sans-serif' }}
                />
              </div>

              <div>
                <label
                  className="block text-xs font-bold text-gray-700 mb-1.5"
                  style={{ fontFamily: 'Noto Sans JP, sans-serif' }}
                >
                  一番困っていること <span className="text-[#C0392B]">*</span>
                </label>
                <select
                  name="main_concern"
                  required
                  value={concern}
                  onChange={(e) => setConcern(e.target.value)}
                  className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:border-[#C0392B] transition-colors bg-white cursor-pointer"
                  style={{ fontFamily: 'Noto Sans JP, sans-serif' }}
                >
                  <option value="">選択してください</option>
                  {concernOptions.map((o) => (
                    <option key={o} value={o}>{o}</option>
                  ))}
                </select>
              </div>

              <div className="md:col-span-2">
                <label
                  className="block text-xs font-bold text-gray-700 mb-1.5"
                  style={{ fontFamily: 'Noto Sans JP, sans-serif' }}
                >
                  自由記入（500文字以内）
                </label>
                <textarea
                  name="message"
                  rows={3}
                  maxLength={500}
                  placeholder="現在の会議で感じている課題を自由にご記入ください"
                  className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:border-[#C0392B] transition-colors resize-none"
                  style={{ fontFamily: 'Noto Sans JP, sans-serif' }}
                />
              </div>

              {status === 'error' && (
                <div className="md:col-span-2">
                  <p className="text-xs text-red-500" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>
                    送信に失敗しました。時間をおいて再度お試しください。
                  </p>
                </div>
              )}

              <div className="md:col-span-2">
                <button
                  type="submit"
                  disabled={status === 'loading'}
                  className="w-full py-4 bg-[#C0392B] text-white font-bold text-base rounded-md hover:bg-[#a93226] transition-all cursor-pointer whitespace-nowrap disabled:opacity-60"
                  style={{ fontFamily: 'Noto Sans JP, sans-serif' }}
                >
                  {status === 'loading' ? '送信中...' : '無料診断を申し込む（5分）→'}
                </button>
                <p
                  className="text-center text-xs text-gray-400 mt-3"
                  style={{ fontFamily: 'Noto Sans JP, sans-serif' }}
                >
                  強引な営業は一切行いません。診断後のお断りも全く問題ありません。
                </p>
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
